import {Injectable} from '@angular/core';
import {Observable, map} from 'rxjs';
import {QlService} from 'features/core/shared/ql/service/ql.service';
import {FilterCriteria} from 'features/core/shared/filter/api/filter.criteria';

/**
 * AI:
 * Status: 'generated'
 * Type: Service
 * SubType: QL Data Service
 * Reason: Kapselt die QL-Abfrage, die für einen Kreditor alle DmDocumentTypes
 *         ermittelt, die an dessen AKTIVEN CAMT-Import-Konfigurationen verlinkt
 *         sind (= die importfähigen Dokumenttypen).
 *
 *         Einstiegspunkt: bm.CreditorCamtImportConfigDocumentType (Join-Entity).
 *         Join 1: creditorCamtImportConfig (Alias 'c') — gefiltert nach
 *                 c.creditorId == creditorId UND c.entityStatus == 3 (ACTIVE).
 *         Join 2: documentType (Alias 'dt') — liefert dt.id und dt.name.
 *
 *         DISTINCT-Strategie: ein Kreditor kann mehrere aktive Configs haben,
 *         die denselben DmDocumentType referenzieren. Die Deduplizierung
 *         erfolgt client-seitig via Map<id, {id, name}> im mapRawRows-Schritt,
 *         da QL keine DISTINCT-Klausel auf entity-Pipes unterstützt.
 *
 *         Speist das Default/Filter des Datei-Upload-Dropdowns.
 */
@Injectable({providedIn: 'root'})
export class BmCamtImportDocumentTypeQlService {

    /** Pipe name used for the document-type query — also the key for result.get(). */
    static readonly PIPE_NAME = 'camtImportDocumentTypes';

    /**
     * EntityStatusEnum ordinal for 'active' (draft=0, pending=1, discarded=2, active=3, obsolete=4, deleted=5).
     * Value verified against EntityStatusEnum in the backend.
     */
    private static readonly ENTITY_STATUS_ACTIVE = 3;

    constructor(private readonly qlService: QlService) {}

    // ====================================================================
    // Public API
    // ====================================================================

    /**
     * Liefert alle DmDocumentTypes, die an den AKTIVEN CAMT-Import-Configs
     * des gegebenen Kreditors verlinkt sind (importfähige Dokumenttypen).
     *
     * Traversal: bm.CreditorCamtImportConfigDocumentType (e)
     *   → creditorCamtImportConfig (c, gefiltert: c.creditorId == creditorId, c.entityStatus == 3)
     *   → documentType (dt, selektiert: dt.id, dt.name)
     *
     * Duplikate (gleicher DmDocumentType über mehrere Configs) werden
     * client-seitig via id-basierter Map entfernt.
     *
     * @param creditorId  Primärschlüssel des bm.Creditor-Eintrags.
     * @returns Observable mit einzigartigen {id, name}-Paaren der Dokumenttypen.
     */
    getCamtImportDocumentTypesForCreditor(creditorId: number): Observable<CamtImportDocumentTypeItem[]> {
        return this.qlService.builder()
            .query({
                name: BmCamtImportDocumentTypeQlService.PIPE_NAME,
                type: 'view',
                start: {
                    type: 'entity',
                    name: 'bm.CreditorCamtImportConfigDocumentType',
                    as: 'e',
                    joins: [
                        {
                            // Join auf die CAMT-Import-Konfiguration.
                            // name = FK-Attributname ohne Id-Suffix (QL-Regel: join-name = Relationsname).
                            // Filter auf c.creditorId (FK-Suffix in Filter) und c.entityStatus == 3 (ACTIVE).
                            joinType: 'inner',
                            type: 'entity',
                            name: 'creditorCamtImportConfig',
                            as: 'c',
                            filterCriteria: [
                                FilterCriteria.create('c.creditorId', FilterCriteria.cOperatorEqual, creditorId),
                                FilterCriteria.create(
                                    'c.entityStatus',
                                    FilterCriteria.cOperatorEqual,
                                    BmCamtImportDocumentTypeQlService.ENTITY_STATUS_ACTIVE
                                )
                            ]
                        },
                        {
                            // Join auf den verlinkten DmDocumentType.
                            // name = FK-Attributname ohne Id-Suffix (QL-Regel: join-name = Relationsname).
                            joinType: 'inner',
                            type: 'entity',
                            name: 'documentType',
                            as: 'dt'
                        }
                    ]
                }
            })
            .execute()
            .pipe(
                map(result => this.mapRawRows(result.get(BmCamtImportDocumentTypeQlService.PIPE_NAME)))
            );
    }

    // ====================================================================
    // Private helpers
    // ====================================================================

    /**
     * Mapt rohe QL-Zeilen auf {@link CamtImportDocumentTypeItem} und
     * dedupliziert nach dt.id (ein Typ kann über mehrere Configs verlinkt sein).
     *
     * Ergebnis-Keys: 'dt.id' und 'dt.name' — geliefert vom Inner Join auf
     * alias 'dt' (documentType-Relation).
     */
    private mapRawRows(rawItems: any[]): CamtImportDocumentTypeItem[] {
        const seen = new Map<number, CamtImportDocumentTypeItem>();
        for (const item of (rawItems ?? [])) {
            const id: number = item['dt.id'] as number;
            if (!seen.has(id)) {
                seen.set(id, {
                    id,
                    name: item['dt.name'] as string
                });
            }
        }
        return Array.from(seen.values());
    }
}

// ====================================================================
// Public types
// ====================================================================

/**
 * Minimal descriptor for a DmDocumentType returned by the CAMT-import
 * document-type query. Feeds the file-upload dropdown.
 */
export interface CamtImportDocumentTypeItem {
    /** Primärschlüssel des dm.DmDocumentType. */
    id: number;
    /** Anzeigename des Dokumenttyps. */
    name: string;
}
